import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format, addDays, startOfTomorrow } from "date-fns";
import { computeWordDiff } from "../../lib/diff";
import WordDiff from "../../components/WordDiff";
import { getMediaType } from "../../lib/media";
import { uploadToCloudinary } from "../../lib/cloudinary";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Calendar } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Send,
  Image as ImageIcon,
  Calendar as CalendarIcon,
  Loader2,
  UploadCloud,
  Film,
  Check,
  X,
  MessageSquare,
  Trash2,
  History,
  ChevronRight,
  Eye,
  CheckCircle2,
  Clock,
  FileText,
  GitCompare,
  MessageCircle,
  Link as LinkIcon,
  RotateCcw,
} from "lucide-react";
import LinkedInPostPreview from "../../components/LinkedInPostPreview";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POST_TYPES = [
  { value: "thought_leadership", label: "Thought Leadership" },
  { value: "promotional", label: "Promotional" },
  { value: "educational", label: "Educational" },
  { value: "engagement", label: "Engagement" },
  { value: "behind_the_scenes", label: "Behind the Scenes" },
  { value: "case_study", label: "Case Study" },
  { value: "other", label: "Other" },
];

// Approval Stage Progress Steps
const APPROVAL_STAGES = [
  { key: "draft", label: "Draft", icon: FileText },
  { key: "internal_review", label: "Internal Review", icon: Eye },
  { key: "client_review", label: "Client Review", icon: Clock },
  { key: "approved", label: "Approved", icon: CheckCircle2 },
  { key: "scheduled", label: "Scheduled", icon: CalendarIcon },
];

// Maps any post status → the matching APPROVAL_STAGES key
const STATUS_TO_STAGE = {
  idea: "draft",
  draft: "draft",
  internal_review: "internal_review",
  pending_approval: "client_review",
  client_review: "client_review",
  change_requested: "client_review",
  rejected: "client_review",
  approved: "approved",
  scheduled: "scheduled",
  posted: "scheduled",
};

// Stage Progress Bar Component
const StageProgressBar = ({ currentStage }) => {
  const getStageIndex = (stage) => {
    const mapped = STATUS_TO_STAGE[stage] || "draft";
    return APPROVAL_STAGES.findIndex((s) => s.key === mapped);
  };

  const currentIdx = getStageIndex(currentStage);

  return (
    <div className="flex items-center justify-between w-full px-2">
      {APPROVAL_STAGES.map((stage, idx) => {
        const Icon = stage.icon;
        const isCompleted = idx < currentIdx;
        const isCurrent = idx === currentIdx;

        return (
          <React.Fragment key={stage.key}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                  isCompleted
                    ? "bg-[#556B2F] border-[#556B2F] text-white"
                    : isCurrent
                      ? "bg-[#556B2F] border-[#556B2F] text-white"
                      : "bg-slate-100 border-slate-300 text-slate-400"
                }`}
              >
                {isCompleted ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Icon className="w-5 h-5" />
                )}
              </div>
              <span
                className={`text-xs mt-2 font-medium ${
                  isCurrent || isCompleted
                    ? "text-[#556B2F] dark:text-[#b8cf88]"
                    : "text-slate-400"
                }`}
              >
                {stage.label}
              </span>
            </div>
            {idx < APPROVAL_STAGES.length - 1 && (
              <div
                className={`flex-1 h-1 mx-2 rounded ${
                  isCompleted ? "bg-[#556B2F]" : "bg-slate-200"
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── Client-side word-level diff ──────────────────────────────────────────────
// Version History Component
const VersionHistoryPanel = ({ postId, isOpen, onClose, onRestore }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [diffResult, setDiffResult] = useState(null);
  const [showDiff, setShowDiff] = useState(false);

  useEffect(() => {
    if (isOpen && postId) {
      fetchVersions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, postId]);

  const fetchVersions = async () => {
    try {
      const response = await axios.get(`${API_URL}/posts/${postId}/versions`);
      setVersions(response.data.versions);
    } catch (error) {
      toast.error("Failed to fetch version history");
    } finally {
      setLoading(false);
    }
  };

  const handleVersionSelect = (version) => {
    if (selectedVersions.includes(version.version)) {
      setSelectedVersions(
        selectedVersions.filter((v) => v !== version.version),
      );
    } else if (selectedVersions.length < 2) {
      setSelectedVersions([...selectedVersions, version.version]);
    }
  };

  const compareDiff = async () => {
    if (selectedVersions.length !== 2) return;
    const [v1, v2] = selectedVersions.sort((a, b) => a - b);

    // Client-side diff — no server round-trip needed.
    const versionA = versions.find((v) => v.version === v1);
    const versionB = versions.find((v) => v.version === v2);
    if (!versionA || !versionB) {
      toast.error("Selected versions not found");
      return;
    }
    setDiffResult({
      v1: versionA,
      v2: versionB,
      diff: computeWordDiff(versionA.content, versionB.content),
    });
    setShowDiff(true);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Version History
          </DialogTitle>
        </DialogHeader>

        {!showDiff ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Select two versions to compare differences.
            </p>

            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : versions.length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                No version history available
              </p>
            ) : (
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {versions.map((version) => (
                    <div
                      key={version.id}
                      onClick={() => handleVersionSelect(version)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedVersions.includes(version.version)
                          ? "border-[#556B2F] bg-[#f0f4e8] dark:bg-[#556B2F]/10"
                          : "border-slate-200 dark:border-slate-700 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">v{version.version}</Badge>
                          <span className="text-sm font-medium text-slate-900 dark:text-white">
                            {version.author_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">
                            {version.created_at
                              ? format(new Date(version.created_at), "PPp")
                              : ""}
                          </span>
                          {onRestore && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 gap-1 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRestore(version);
                              }}
                            >
                              <RotateCcw className="w-3 h-3" />
                              Restore
                            </Button>
                          )}
                        </div>
                      </div>
                      {version.summary && (
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {version.summary}
                        </p>
                      )}
                      <p className="text-sm text-slate-500 mt-2 line-clamp-2">
                        {version.content}
                      </p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button
                onClick={compareDiff}
                disabled={selectedVersions.length !== 2}
                className="bg-[#556B2F] hover:bg-[#3d4f22]"
              >
                <GitCompare className="w-4 h-4 mr-2" />
                Compare Selected
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm">
              <Badge>v{diffResult.v1.version}</Badge>
              <ChevronRight className="w-4 h-4" />
              <Badge>v{diffResult.v2.version}</Badge>
            </div>

            <ScrollArea className="h-[400px] p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="whitespace-pre-wrap font-mono text-sm">
                {diffResult.diff.map((item, idx) => (
                  <span
                    key={idx}
                    className={
                      item.type === "added"
                        ? "bg-[#b8cf88] dark:bg-[#2d3a1a] text-[#3d4f22] dark:text-[#b8cf88]"
                        : item.type === "removed"
                          ? "bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200 line-through"
                          : ""
                    }
                  >
                    {item.text}{" "}
                  </span>
                ))}
              </div>
            </ScrollArea>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDiff(false)}>
                Back
              </Button>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const RemoveMediaButton = ({ onRemove }) => (
  <button
    type="button"
    onClick={onRemove}
    className="absolute top-2 right-2 z-10 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
  >
    <X className="w-4 h-4" />
  </button>
);

const MediaItem = ({ url, onRemove }) => {
  const initialType = getMediaType(url);
  // If an "image" URL fails to load, fall back to showing it as a link.
  const [type, setType] = useState(initialType);

  if (type === "image") {
    return (
      <div className="relative group">
        <img
          src={url}
          alt=""
          className="w-full h-32 object-cover rounded-lg bg-slate-100 dark:bg-slate-800"
          onError={() => setType("link")}
          referrerPolicy="no-referrer"
        />
        <RemoveMediaButton onRemove={onRemove} />
      </div>
    );
  }

  if (type === "video") {
    return (
      <div className="relative group">
        <video
          src={url}
          controls
          className="w-full h-32 object-cover rounded-lg bg-black"
        />
        <RemoveMediaButton onRemove={onRemove} />
      </div>
    );
  }

  if (type === "pdf") {
    return (
      <div className="relative group flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800">
        <FileText className="w-5 h-5 text-red-500 shrink-0" />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-[#556B2F] hover:underline truncate flex-1 min-w-0"
        >
          {url.split("/").pop() || "Document.pdf"}
        </a>
        <RemoveMediaButton onRemove={onRemove} />
      </div>
    );
  }

  return (
    <div className="relative group flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800">
      <LinkIcon className="w-4 h-4 text-slate-400 shrink-0" />
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-[#556B2F] hover:underline truncate flex-1 min-w-0"
      >
        {url}
      </a>
      <RemoveMediaButton onRemove={onRemove} />
    </div>
  );
};

const PostEditor = () => {
  const { postId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditing = !!postId;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [post, setPost] = useState(null);
  // The client's pending suggested edit (a pending version), if any.
  const [pendingEdit, setPendingEdit] = useState(null);
  const [reviewingEdit, setReviewingEdit] = useState(false);

  // Version History
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  // Set when the user restores an older version; cleared on save. Drives the
  // "Restored from vN" banner and the version summary recorded on save.
  const [restoredFromVersion, setRestoredFromVersion] = useState(null);
  const [showLinkedInPreview, setShowLinkedInPreview] = useState(false);

  const [formData, setFormData] = useState({
    client_id: searchParams.get("client") || "",
    copy: "",
    image_urls: [],
    post_type: "other",
    scheduled_date: searchParams.get("date")
      ? new Date(searchParams.get("date"))
      : null,
    scheduled_time: "",
    is_scheduled: false,
    internal_notes: "",
    status: "draft",
    assigned_reviewer_id: "",
  });

  const [newImageUrl, setNewImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  // Upload selected files to Cloudinary and append their URLs to the post.
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file
    if (!files.length) return;

    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        urls.push(await uploadToCloudinary(file));
      }
      setFormData((prev) => ({
        ...prev,
        image_urls: [...prev.image_urls, ...urls],
      }));
      toast.success(
        urls.length > 1 ? `${urls.length} files uploaded` : "File uploaded",
      );
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Stage Change
  const [stageChangeOpen, setStageChangeOpen] = useState(false);
  const [newStage, setNewStage] = useState("");
  const [stageNotes, setStageNotes] = useState("");

  useEffect(() => {
    fetchClients();
    if (isEditing) {
      fetchPost();
      fetchComments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, isEditing]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data.clients);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API_URL}/posts/${postId}`);
      const postData = response.data.post;
      setPost(postData);
      setFormData({
        client_id: postData.client_id,
        copy: postData.copy,
        image_urls: postData.image_urls || [],
        post_type: postData.post_type,
        scheduled_date: postData.scheduled_date
          ? new Date(postData.scheduled_date)
          : null,
        scheduled_time: postData.scheduled_time || "",
        is_scheduled: postData.is_scheduled || false,
        internal_notes: postData.internal_notes || "",
        status: postData.status,
        assigned_reviewer_id: postData.assigned_reviewer_id || "",
      });
      setPendingEdit(response.data.pending_edit || null);
    } catch (error) {
      toast.error("Failed to fetch post");
      navigate("/agency/calendar");
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API_URL}/posts/${postId}/comments`);
      setComments(response.data.comments);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    }
  };

  // Change Approval Stage
  const handleStageChange = async () => {
    try {
      await axios.patch(`${API_URL}/posts/${postId}/stage`, {
        new_stage: newStage,
        notes: stageNotes,
      });
      toast.success(`Post moved to ${newStage.replace(/_/g, " ")}`);
      setStageChangeOpen(false);
      setStageNotes("");
      fetchPost();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change stage");
    }
  };

  // Review a client's suggested edit (pending version)
  const acceptClientEdit = async () => {
    if (!pendingEdit) return;
    setReviewingEdit(true);
    try {
      await axios.post(
        `${API_URL}/posts/${postId}/versions/${pendingEdit.version}/accept`,
      );
      toast.success("Client changes accepted — the post now reflects them");
      await fetchPost();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to accept changes");
    } finally {
      setReviewingEdit(false);
    }
  };

  const declineClientEdit = async () => {
    if (!pendingEdit) return;
    const reason = window.prompt(
      "Optionally, tell the client why you're declining these changes:",
      "",
    );
    // prompt returns null when cancelled — abort in that case
    if (reason === null) return;
    setReviewingEdit(true);
    try {
      await axios.post(
        `${API_URL}/posts/${postId}/versions/${pendingEdit.version}/decline${
          reason ? `?reason=${encodeURIComponent(reason)}` : ""
        }`,
      );
      toast.success("Client changes declined");
      await fetchPost();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to decline changes");
    } finally {
      setReviewingEdit(false);
    }
  };

  // Non-destructive rollback: load the chosen version's text into the editor.
  // Nothing is persisted until the user saves, which records a new version.
  const handleRestoreVersion = (version) => {
    setFormData((prev) => ({ ...prev, copy: version.content }));
    setRestoredFromVersion(version.version);
    setShowVersionHistory(false);
    toast.success(
      `Loaded v${version.version}. Review and Save to create a new version.`,
    );
  };

  const handleSave = async (submitStage = null) => {
    if (!formData.client_id || !formData.copy) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        status: submitStage || formData.status,
        scheduled_date: formData.scheduled_date?.toISOString(),
        scheduled_time: formData.scheduled_time || null,
        is_scheduled: !!formData.scheduled_date,
        ...(restoredFromVersion
          ? { change_summary: `Restored from v${restoredFromVersion}` }
          : {}),
      };

      if (isEditing) {
        await axios.put(`${API_URL}/posts/${postId}`, payload);
        toast.success(
          submitStage
            ? `Post submitted for ${submitStage.replace(/_/g, " ")}!`
            : "Post saved!",
        );
        setRestoredFromVersion(null);
        fetchPost();
      } else {
        const response = await axios.post(`${API_URL}/posts`, payload);
        toast.success(
          submitStage
            ? `Post submitted for ${submitStage.replace(/_/g, " ")}!`
            : "Post created!",
        );
        navigate(`/agency/posts/${response.data.post.id}`);
        return;
      }

      if (submitStage === "client_review") {
        navigate("/agency/calendar");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save post");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      await axios.delete(`${API_URL}/posts/${postId}`);
      toast.success("Post deleted");
      navigate("/agency/calendar");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;

    try {
      const response = await axios.post(`${API_URL}/posts/${postId}/comments`, {
        content: newComment,
      });
      setComments([...comments, response.data.comment]);
      setNewComment("");
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const charCount = formData.copy.length;
  const maxChars = 3000;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
      </div>
    );
  }

  const selectedClient = clients.find((c) => c.id === formData.client_id);
  const currentStage = post?.approval_stage || post?.status || "draft";

  // Determine valid next stages
  const getValidNextStages = () => {
    const transitions = {
      draft: ["internal_review", "client_review"],
      internal_review: ["draft", "client_review"],
      change_requested: ["draft", "internal_review", "client_review"],
      approved: ["scheduled"],
      scheduled: ["posted"],
    };
    return transitions[currentStage] || [];
  };

  return (
    <div className="space-y-6" data-testid="post-editor">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              {isEditing ? "Edit Post" : "Create Post"}
            </h1>
            {selectedClient && (
              <p className="text-slate-600 dark:text-slate-400">
                For {selectedClient.name}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {isEditing && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowVersionHistory(true)}
                data-testid="version-history-btn"
              >
                <History className="w-4 h-4 mr-2" />
                History
              </Button>
              <Button
                variant="outline"
                onClick={handleDelete}
                className="text-red-600 hover:text-red-700"
                data-testid="delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </>
          )}
          <Button
            variant="outline"
            onClick={() => handleSave(null)}
            disabled={saving}
            data-testid="save-draft-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </Button>

          {/* Submit for different stages */}
          {currentStage === "draft" && (
            <>
              <Button
                variant="outline"
                onClick={() => handleSave("internal_review")}
                disabled={saving}
                data-testid="submit-internal-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Internal Review
              </Button>
              <Button
                onClick={() => handleSave("client_review")}
                disabled={saving}
                className="bg-[#556B2F] hover:bg-[#3d4f22] text-white"
                data-testid="submit-approval-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Send to Client"}
              </Button>
            </>
          )}

          {currentStage === "internal_review" && (
            <Button
              onClick={() => handleSave("client_review")}
              disabled={saving}
              className="bg-[#556B2F] hover:bg-[#3d4f22] text-white"
              data-testid="submit-client-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Send to Client
            </Button>
          )}

          {currentStage === "approved" && (
            <Button
              onClick={() => handleSave("scheduled")}
              disabled={saving || !formData.scheduled_date}
              className="bg-[#3d4f22] hover:bg-[#3d4f22] text-white"
              data-testid="schedule-btn"
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Schedule
            </Button>
          )}
        </div>
      </div>

      {/* Stage Progress Bar */}
      {isEditing && (
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-6">
          <StageProgressBar currentStage={currentStage} />
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Editor */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client & Type Selection */}
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardContent className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <Select
                    value={formData.client_id}
                    onValueChange={(v) =>
                      setFormData({ ...formData, client_id: v })
                    }
                  >
                    <SelectTrigger data-testid="client-select">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Post Type</Label>
                  <Select
                    value={formData.post_type}
                    onValueChange={(v) =>
                      setFormData({ ...formData, post_type: v })
                    }
                  >
                    <SelectTrigger data-testid="type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {POST_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Post Copy */}
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Post Copy</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 border-[#0a66c2] text-[#0a66c2] hover:bg-[#0a66c2]/5 dark:border-[#0a66c2] dark:text-[#5b9bd5]"
                  onClick={() => setShowLinkedInPreview(true)}
                  type="button"
                >
                  <span className="font-bold text-xs leading-none bg-[#0a66c2] text-white rounded px-1 py-0.5">in</span>
                  Preview
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {restoredFromVersion && (
                <div className="flex items-center gap-2 rounded-lg border border-[#b8cf88] bg-[#f0f4e8] dark:border-[#556B2F] dark:bg-[#556B2F]/10 px-3 py-2 text-sm text-[#3d4f22] dark:text-[#b8cf88]">
                  <RotateCcw className="w-4 h-4 shrink-0" />
                  <span>
                    Editing a restored copy of{" "}
                    <strong>v{restoredFromVersion}</strong>. Save to keep it as a
                    new version.
                  </span>
                </div>
              )}
              <div className="space-y-2 relative">
                <Textarea
                  value={formData.copy}
                  onChange={(e) =>
                    setFormData({ ...formData, copy: e.target.value })
                  }
                  placeholder="Write your LinkedIn post here...

Tips:
• Start with a hook to grab attention
• Use line breaks for readability
• Include a clear call to action
• Add relevant hashtags"
                  rows={12}
                  className="font-sans text-base leading-relaxed resize-none"
                  data-testid="post-copy-input"
                />

                <div className="flex items-center justify-between text-sm">
                  <span
                    className={
                      charCount > maxChars ? "text-red-500" : "text-slate-500"
                    }
                  >
                    {charCount} / {maxChars} characters
                  </span>
                  {charCount > 0 && charCount <= maxChars && (
                    <span className="text-[#556B2F] flex items-center gap-1">
                      <Check className="w-4 h-4" /> Good length
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Client proposed changes (track-changes review) */}
          {isEditing && pendingEdit && (
            <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-amber-900 dark:text-amber-200">
                  <MessageCircle className="w-5 h-5" />
                  Client proposed changes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-amber-900/80 dark:text-amber-200/80">
                  The client suggested the following edits. Accept to make them
                  the current copy, or decline to keep the existing version.
                </p>
                <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-white/70 dark:bg-slate-900/40 p-4">
                  <WordDiff oldText={post?.copy || ""} newText={pendingEdit.content} />
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={declineClientEdit}
                    disabled={reviewingEdit}
                    className="text-red-600 hover:bg-red-50"
                    data-testid="decline-edit-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Decline
                  </Button>
                  <Button
                    onClick={acceptClientEdit}
                    disabled={reviewingEdit}
                    className="bg-[#556B2F] hover:bg-[#3d4f22] text-white"
                    data-testid="accept-edit-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {reviewingEdit ? "Working..." : "Accept changes"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Media & Links Section */}
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Media & Links
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current URLs */}
              {formData.image_urls.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {formData.image_urls.map((url, idx) => (
                    <MediaItem
                      key={idx}
                      url={url}
                      onRemove={() =>
                        setFormData({
                          ...formData,
                          image_urls: formData.image_urls.filter((_, i) => i !== idx),
                        })
                      }
                    />
                  ))}
                </div>
              )}

              {/* Upload from device (Cloudinary) */}
              <div className="space-y-2">
                <Label>Upload media</Label>
                <label
                  className={`flex flex-col items-center justify-center gap-2 w-full px-4 py-6 border-2 border-dashed rounded-lg text-center transition-colors ${
                    uploading
                      ? "border-slate-200 dark:border-slate-700 cursor-wait opacity-70"
                      : "border-slate-300 dark:border-slate-600 hover:border-[#556B2F] cursor-pointer"
                  }`}
                  data-testid="media-upload-dropzone"
                >
                  <input
                    type="file"
                    accept="image/*,video/*,application/pdf"
                    multiple
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    data-testid="media-file-input"
                  />
                  {uploading ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin text-[#556B2F]" />
                      <span className="text-sm text-slate-500">Uploading…</span>
                    </>
                  ) : (
                    <>
                      <UploadCloud className="w-6 h-6 text-[#556B2F]" />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Click to upload images, videos or PDFs
                      </span>
                      <span className="text-xs text-slate-500 flex items-center gap-2">
                        <ImageIcon className="w-3 h-3" /> Images
                        <Film className="w-3 h-3" /> Video
                        <FileText className="w-3 h-3" /> PDF · up to 100MB
                      </span>
                    </>
                  )}
                </label>
              </div>

              {/* URL Input */}
              <div className="space-y-2">
                <Label>Or add by URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="https://example.com/image.jpg or any link"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (newImageUrl.trim()) {
                          setFormData({
                            ...formData,
                            image_urls: [...formData.image_urls, newImageUrl.trim()],
                          });
                          setNewImageUrl("");
                        }
                      }
                    }}
                    data-testid="image-url-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (newImageUrl.trim()) {
                        setFormData({
                          ...formData,
                          image_urls: [...formData.image_urls, newImageUrl.trim()],
                        });
                        setNewImageUrl("");
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Images show as previews — any other URL shows as a clickable link
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Comments (Edit Mode) */}
          {isEditing && (
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Comments & Revisions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {comments.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No comments yet
                  </p>
                ) : (
                  <ScrollArea className="max-h-64">
                    <div className="space-y-3">
                      {comments.map((comment) => (
                        <div
                          key={comment.id}
                          className={`p-3 rounded-lg ${
                            comment.user_role === "client"
                              ? "bg-[#f0f4e8] dark:bg-[#556B2F]/10 ml-8"
                              : "bg-slate-50 dark:bg-slate-800 mr-8"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm text-slate-900 dark:text-white">
                              {comment.user_name}
                            </span>
                            <Badge
                              variant="outline"
                              className="text-xs capitalize"
                            >
                              {comment.user_role}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-700 dark:text-slate-300">
                            {comment.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}

                <div className="flex gap-2">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    onKeyDown={(e) => e.key === "Enter" && addComment()}
                    data-testid="comment-input"
                  />
                  <Button
                    onClick={addComment}
                    variant="outline"
                    data-testid="add-comment-btn"
                  >
                    Send
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Schedule & Notes */}
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg">Schedule</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Scheduled Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${!formData.scheduled_date ? "text-muted-foreground" : ""}`}
                      data-testid="schedule-date-btn"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2 shrink-0" />
                      {formData.scheduled_date
                        ? format(formData.scheduled_date, "EEE, MMM d, yyyy")
                        : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    {/* Quick-select strip */}
                    <div className="flex gap-1 p-2 border-b border-slate-100 dark:border-slate-800">
                      {[
                        { label: "Today", date: new Date() },
                        { label: "Tomorrow", date: startOfTomorrow() },
                        { label: "+1 Week", date: addDays(new Date(), 7) },
                        { label: "+2 Weeks", date: addDays(new Date(), 14) },
                      ].map(({ label, date }) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              scheduled_date: date,
                              is_scheduled: true,
                            })
                          }
                          className="text-xs px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-[#556B2F] hover:text-white transition-colors"
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <Calendar
                      mode="single"
                      selected={formData.scheduled_date}
                      onSelect={(date) =>
                        setFormData({
                          ...formData,
                          scheduled_date: date,
                          is_scheduled: !!date,
                        })
                      }
                      disabled={{ before: new Date() }}
                      initialFocus
                    />
                    {formData.scheduled_date && (
                      <div className="p-2 border-t border-slate-100 dark:border-slate-800">
                        <button
                          type="button"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              scheduled_date: null,
                              scheduled_time: "",
                              is_scheduled: false,
                            })
                          }
                          className="w-full text-xs text-red-500 hover:text-red-600 py-1 rounded-md hover:bg-red-50 transition-colors"
                        >
                          Clear date
                        </button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time Picker */}
              <div className="space-y-2">
                <Label>Scheduled Time</Label>
                <div className="flex gap-2">
                  <select
                    value={formData.scheduled_time?.split(":")[0] || ""}
                    onChange={(e) => {
                      const hour = e.target.value;
                      const minute =
                        formData.scheduled_time?.split(":")[1] || "00";
                      setFormData({
                        ...formData,
                        scheduled_time: hour ? `${hour}:${minute}` : "",
                      });
                    }}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="schedule-hour"
                  >
                    <option value="">Hour</option>
                    {[...Array(24)].map((_, i) => (
                      <option key={i} value={i.toString().padStart(2, "0")}>
                        {i === 0
                          ? "12 AM"
                          : i < 12
                            ? `${i} AM`
                            : i === 12
                              ? "12 PM"
                              : `${i - 12} PM`}
                      </option>
                    ))}
                  </select>
                  <select
                    value={formData.scheduled_time?.split(":")[1] || ""}
                    onChange={(e) => {
                      const hour =
                        formData.scheduled_time?.split(":")[0] || "09";
                      setFormData({
                        ...formData,
                        scheduled_time: `${hour}:${e.target.value}`,
                      });
                    }}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="schedule-minute"
                  >
                    <option value="">Min</option>
                    {["00", "15", "30", "45"].map((m) => (
                      <option key={m} value={m}>
                        :{m}
                      </option>
                    ))}
                  </select>
                </div>
                {formData.scheduled_date && formData.scheduled_time && (
                  <p className="text-sm text-[#3d4f22] dark:text-[#b8cf88] mt-2">
                    Scheduled for:{" "}
                    {format(formData.scheduled_date, "MMMM d, yyyy")} at{" "}
                    {(() => {
                      const [h, m] = formData.scheduled_time.split(":");
                      const hour = parseInt(h);
                      const ampm = hour >= 12 ? "PM" : "AM";
                      const displayHour =
                        hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                      return `${displayHour}:${m} ${ampm}`;
                    })()}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Internal Notes</Label>
                <Textarea
                  value={formData.internal_notes}
                  onChange={(e) =>
                    setFormData({ ...formData, internal_notes: e.target.value })
                  }
                  placeholder="Notes for your team (not visible to client)"
                  rows={3}
                  className="text-sm"
                  data-testid="internal-notes-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Stage History */}
          {isEditing && post?.stage_history?.length > 0 && (
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Stage History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-48">
                  <div className="space-y-2">
                    {[...post.stage_history].reverse().map((entry, idx) => (
                      <div
                        key={idx}
                        className="text-sm border-l-2 border-slate-200 dark:border-slate-700 pl-3 py-1"
                      >
                        <p className="font-medium capitalize text-slate-900 dark:text-white">
                          {entry.stage?.replace(/_/g, " ")}
                        </p>
                        {entry.notes && (
                          <p className="text-slate-500">{entry.notes}</p>
                        )}
                        <p className="text-xs text-slate-400">
                          {format(new Date(entry.changed_at), "PPp")}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Status */}
          {isEditing && (
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg">Current Status</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge className="text-sm capitalize px-3 py-1">
                  {currentStage?.replace(/_/g, " ")}
                </Badge>

                {/* Quick Stage Change */}
                {getValidNextStages().length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-slate-500">Move to:</p>
                    <div className="flex flex-wrap gap-2">
                      {getValidNextStages().map((stage) => (
                        <Button
                          key={stage}
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setNewStage(stage);
                            setStageChangeOpen(true);
                          }}
                          className="text-xs capitalize"
                        >
                          {stage.replace(/_/g, " ")}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* LinkedIn Preview Dialog */}
      <Dialog open={showLinkedInPreview} onOpenChange={setShowLinkedInPreview}>
        <DialogContent className="max-w-xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <span className="font-bold text-sm bg-[#0a66c2] text-white rounded px-1.5 py-0.5">in</span>
              LinkedIn Post Preview
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-1">
            <LinkedInPostPreview
              content={formData.copy}
              attachedMedia={formData.image_urls[0] || null}
              clientName={clients.find(c => c.id === formData.client_id)?.name || 'Client Name'}
            />
            <p className="text-xs text-slate-400 text-center mt-3">
              Approximate preview — actual appearance may vary.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Version History Modal */}
      <VersionHistoryPanel
        postId={postId}
        isOpen={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        onRestore={handleRestoreVersion}
      />

      {/* Stage Change Dialog */}
      <Dialog open={stageChangeOpen} onOpenChange={setStageChangeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Change Stage to {newStage?.replace(/_/g, " ")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={stageNotes}
                onChange={(e) => setStageNotes(e.target.value)}
                placeholder="Add notes about this stage change..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStageChangeOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleStageChange}
              className="bg-[#556B2F] hover:bg-[#3d4f22]"
            >
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PostEditor;
